// src/features/portfolio-optimization-tools/my-strategies/components/ComparativeBacktestChart.tsx
//
// The "truth overlay": rebases the ML strategy's net equity curve and the
// benchmark to a common base of 100 (time-weighted, cash-flow neutral) and
// plots them on DUAL, color-matched axes — the emerald strategy on the left,
// the amber dashed benchmark on its own right-hand scale. A single shared axis
// squashed the benchmark into an unreadable flat line whenever the strategy
// compounded far above it; the independent right scale keeps the benchmark's
// own trajectory (draw-downs, recoveries) legible. The scale-independent read
// of cumulative alpha lives in the tooltip (ratio of the two indices). A dashed
// vertical marker flags the exact date live out-of-sample tracking began;
// everything to its right is real, forward-tracked money.

import { useMemo } from 'react';
import {
    Area,
    CartesianGrid,
    ComposedChart,
    Legend,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';

import type { EquityCurvePoint } from '../../multi-asset-portfolio-optimizer/api';
import { rebaseToTWRR } from './performanceMath';

// Growth-of-100 index formatter, e.g. "187.4".
const indexFormatter = (value: number): string => value.toFixed(1);

// Signed percentage growth vs the common base of 100.
const growthPct = (indexValue: number): string => {
    const pct = indexValue - 100;
    return `${pct >= 0 ? '+' : ''}${pct.toFixed(1)}%`;
};

interface MergedPoint {
    date: string;
    port_idx: number;
    bench_idx: number;
    isLive: boolean;
}

interface CustomTooltipProps {
    active?: boolean;
    label?: string | number;
    payload?: { dataKey: string; value: number; payload: MergedPoint }[];
    benchmarkName?: string;
}

const CustomTooltip = ({
    active,
    payload,
    label,
    benchmarkName = 'Benchmark',
}: CustomTooltipProps) => {
    if (!active || !payload || payload.length < 2) return null;

    const portIdx =
        payload.find((p) => p.dataKey === 'port_idx')?.value ?? 0;
    const benchIdx =
        payload.find((p) => p.dataKey === 'bench_idx')?.value ?? 0;
    const isLive = payload[0].payload.isLive;
    // Cumulative relative outperformance in percent (ratio of the indices).
    const relOut = benchIdx > 0 ? (portIdx / benchIdx - 1) * 100 : 0;
    const isWinning = relOut >= 0;

    return (
        <div className="bg-slate-900 border border-slate-700 p-4 rounded-xl shadow-2xl min-w-[260px] z-50">
            <div className="flex justify-between items-center border-b border-slate-800 pb-2 mb-3">
                <p className="text-slate-400 text-xs font-bold uppercase tracking-wider">
                    {label
                        ? new Date(label).toLocaleDateString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                          })
                        : ''}
                </p>
                {isLive && (
                    <span className="text-[9px] font-bold bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 px-2 py-0.5 rounded uppercase tracking-wider">
                        LIVE OOS
                    </span>
                )}
            </div>
            <div className="space-y-2">
                <div className="flex justify-between items-center gap-4">
                    <span className="text-emerald-400 text-xs font-bold">
                        ML Strategy (Net)
                    </span>
                    <span className="text-white font-mono font-bold text-sm">
                        {indexFormatter(portIdx)}{' '}
                        <span className="text-emerald-400/80 text-[11px]">
                            ({growthPct(portIdx)})
                        </span>
                    </span>
                </div>
                <div className="flex justify-between items-center gap-4">
                    <span className="text-amber-400 text-xs font-semibold">
                        {benchmarkName} (Benchmark)
                    </span>
                    <span className="text-slate-300 font-mono text-xs">
                        {indexFormatter(benchIdx)}{' '}
                        <span className="text-amber-400/70 text-[11px]">
                            ({growthPct(benchIdx)})
                        </span>
                    </span>
                </div>
                <div className="h-px w-full bg-slate-800 my-2" />
                <div className="flex justify-between items-center gap-4 bg-slate-950 p-2 rounded-lg border border-slate-800">
                    <span className="text-slate-300 text-[11px] font-bold uppercase tracking-wider">
                        Cumulative Alpha
                    </span>
                    <span
                        className={`font-mono font-black text-sm ${
                            isWinning ? 'text-emerald-400' : 'text-red-400'
                        }`}
                    >
                        {isWinning ? '+' : ''}
                        {relOut.toFixed(1)}%
                    </span>
                </div>
            </div>
        </div>
    );
};

interface Props {
    portfolioCurve: (EquityCurvePoint & { is_out_of_sample?: boolean })[];
    benchmarkCurve: EquityCurvePoint[];
    /** Human-readable name of the benchmark, shown in the legend & tooltip. */
    benchmarkName?: string;
}

export const ComparativeBacktestChart = ({
    portfolioCurve,
    benchmarkCurve,
    benchmarkName = 'Benchmark',
}: Props) => {
    // Rebase BOTH series to a common base of 100 (time-weighted, cash-flow
    // neutral) on the full history, zip them together by index (the backend
    // guarantees date-for-date alignment), then sub-sample to keep the SVG
    // light on very long histories.
    const chartData = useMemo<MergedPoint[]>(() => {
        const portRebased = rebaseToTWRR(portfolioCurve);
        const benchRebased = rebaseToTWRR(benchmarkCurve);
        const merged = portRebased.map((pt, i) => ({
            date: pt.date,
            port_idx: pt.index,
            bench_idx: benchRebased[i]?.index ?? pt.index,
            isLive: pt.isLive,
        }));

        if (merged.length <= 800) return merged;
        const step = Math.ceil(merged.length / 800);
        return merged.filter(
            (_, i) => i % step === 0 || i === merged.length - 1,
        );
    }, [portfolioCurve, benchmarkCurve]);

    // The exact date live (out-of-sample) tracking began, if any.
    const liveStartDate = useMemo(
        () => chartData.find((d) => d.isLive)?.date ?? null,
        [chartData],
    );

    // Per-series domains. When the strategy compounds far above the benchmark
    // (e.g. index ~300 vs ~110), a single shared axis squashes the amber line
    // into a near-flat, unreadable trace at the bottom. Giving each series its
    // own scale lets the benchmark's OWN trajectory (draw-downs, recoveries)
    // stay legible. The true cumulative-alpha read now lives in the tooltip
    // (index ratio, scale-independent) rather than the raw vertical gap.
    const boundsFor = (key: 'port_idx' | 'bench_idx'): [number, number] => {
        if (chartData.length === 0) return [90, 110];
        const vals = chartData.map((d) => d[key]);
        const minVal = Math.min(...vals);
        const maxVal = Math.max(...vals);
        const padding = (maxVal - minVal) * 0.08 || maxVal * 0.08 || 5;
        return [Math.max(0, minVal - padding), maxVal + padding];
    };
    const portBounds = useMemo<[number, number]>(
        () => boundsFor('port_idx'),
        [chartData],
    );
    const benchBounds = useMemo<[number, number]>(
        () => boundsFor('bench_idx'),
        [chartData],
    );

    if (!portfolioCurve.length || !benchmarkCurve.length) return null;

    return (
        <div className="w-full h-[380px]">
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                    data={chartData}
                    margin={{ top: 20, right: 12, left: 10, bottom: 0 }}
                >
                    <defs>
                        <linearGradient
                            id="colorPort"
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                        >
                            <stop
                                offset="5%"
                                stopColor="#10b981"
                                stopOpacity={0.4}
                            />
                            <stop
                                offset="95%"
                                stopColor="#10b981"
                                stopOpacity={0}
                            />
                        </linearGradient>
                    </defs>
                    <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#334155"
                        opacity={0.2}
                    />
                    <XAxis
                        dataKey="date"
                        tickFormatter={(v: string) =>
                            new Date(v).toLocaleDateString('en-US', {
                                month: 'short',
                                year: '2-digit',
                            })
                        }
                        stroke="#64748b"
                        fontSize={11}
                        tickMargin={10}
                        minTickGap={60}
                        axisLine={false}
                        tickLine={false}
                    />
                    {/* LEFT axis — Your Strategy (emerald). Growth of 100. */}
                    <YAxis
                        yAxisId="port"
                        domain={portBounds}
                        tickFormatter={(val: number) => val.toFixed(0)}
                        stroke="#10b981"
                        fontSize={11}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                        label={{
                            value: 'STRATEGY (=100)',
                            angle: -90,
                            position: 'insideLeft',
                            fill: '#10b981',
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            style: { textAnchor: 'middle' },
                        }}
                    />
                    {/* RIGHT axis — Benchmark (amber), independent scale so the
                        dashed line's own trajectory is legible even when the
                        strategy compounds far above it. */}
                    <YAxis
                        yAxisId="bench"
                        orientation="right"
                        domain={benchBounds}
                        tickFormatter={(val: number) => val.toFixed(0)}
                        stroke="#f59e0b"
                        fontSize={11}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                        label={{
                            value: 'BENCHMARK (=100)',
                            angle: 90,
                            position: 'insideRight',
                            fill: '#f59e0b',
                            fontSize: 9,
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            style: { textAnchor: 'middle' },
                        }}
                    />
                    <Tooltip
                        content={<CustomTooltip benchmarkName={benchmarkName} />}
                        cursor={{
                            stroke: '#64748b',
                            strokeWidth: 1,
                            strokeDasharray: '4 4',
                        }}
                    />
                    <Legend
                        verticalAlign="top"
                        height={36}
                        iconType="plainline"
                        wrapperStyle={{ fontSize: 12, fontWeight: 600 }}
                    />

                    {/* Baseline at the common starting index of 100 (pinned to
                        the strategy axis). */}
                    <ReferenceLine
                        yAxisId="port"
                        y={100}
                        stroke="#475569"
                        strokeDasharray="2 4"
                    />

                    {/* Live-tracking marker: everything to the right is real. */}
                    {liveStartDate && (
                        <ReferenceLine
                            yAxisId="port"
                            x={liveStartDate}
                            stroke="#818cf8"
                            strokeDasharray="4 4"
                            label={{
                                position: 'insideTopLeft',
                                value: ' LIVE TRACKING',
                                fill: '#818cf8',
                                fontSize: 10,
                                fontWeight: 'bold',
                            }}
                        />
                    )}

                    {/* Portfolio area (glowing emerald) — drawn FIRST so the
                        benchmark line renders on top of the fill and stays
                        clearly visible. */}
                    <Area
                        yAxisId="port"
                        type="monotone"
                        dataKey="port_idx"
                        name="Your Strategy (Net)"
                        stroke="#10b981"
                        strokeWidth={3}
                        fillOpacity={1}
                        fill="url(#colorPort)"
                        isAnimationActive
                        animationDuration={1500}
                        animationEasing="ease-out"
                    />

                    {/* Benchmark line (bright amber, dashed) rendered LAST so
                        it sits above the emerald fill and is easy to read. */}
                    <Line
                        yAxisId="bench"
                        type="monotone"
                        dataKey="bench_idx"
                        name={`${benchmarkName} (Benchmark)`}
                        stroke="#f59e0b"
                        strokeWidth={2.5}
                        strokeDasharray="6 4"
                        dot={false}
                        isAnimationActive={false}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        </div>
    );
};

export default ComparativeBacktestChart;
